import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { customAPI } from "services/utils/CustomAPI";
import { fetchFilterPreference } from "services/utils/filterPreferenceApi";
import { setFilterApi } from "services/Redux/userManagament";
import { showToast } from "services/utils/Status";
import {
  flattenHeaders,
  isGroupedHeaderList,
  regroupHeaders,
} from "./headerGroupUtils";

// Shared "Save View" persistence: lets a table with one or more scopes (e.g.
// a report-type switcher) save/select/delete named column views, backed by
// the same `filter-preference` record every other saved preference already
// uses. Each module passes its own `storageKey` (its namespace inside that
// record's filter_condition) and a `scope` (whatever bucket of views is
// currently visible — pass a constant string for a table with no scopes).
//
// Wire it up alongside SaveViewButton and SavedViewChips (see
// SalaryStatementReport for the reference usage) — none of this touches the
// shared CustomFilter.js.
export const useSavedHeaderViews = ({
  domain,
  userId,
  subModuleId,
  storageKey,
  scope,
  onRestoreScope,
  onViewChange,
  defaultViewId = null,
}) => {
  const dispatch = useDispatch();
  const filterApiRefresh = useSelector((state) => state.userReducer.filterApi);
  const savedViewCacheKey = useMemo(() => {
    if (!storageKey || !userId || !subModuleId) return null;
    return `saved_header_views__${storageKey}__${userId}__${subModuleId}`;
  }, [storageKey, subModuleId, userId]);

  const readSavedViewCache = useCallback(() => {
    if (!savedViewCacheKey) {
      return {
        views: {},
        activeViewIds: {},
        filterPreferenceDetails: {},
      };
    }

    try {
      const cachedValue = localStorage.getItem(savedViewCacheKey);
      if (!cachedValue) {
        return {
          views: {},
          activeViewIds: {},
          filterPreferenceDetails: {},
        };
      }

      const parsed = JSON.parse(cachedValue);
      return {
        views: parsed?.views || {},
        activeViewIds: parsed?.activeViewIds || {},
        filterPreferenceDetails: parsed?.filterPreferenceDetails || {},
      };
    } catch {
      return {
        views: {},
        activeViewIds: {},
        filterPreferenceDetails: {},
      };
    }
  }, [savedViewCacheKey]);

  const writeSavedViewCache = useCallback(
    (views, activeViewIds, preferenceDetails) => {
      if (!savedViewCacheKey) return;

      localStorage.setItem(
        savedViewCacheKey,
        JSON.stringify({
          views,
          activeViewIds,
          filterPreferenceDetails: preferenceDetails || {},
        }),
      );
    },
    [savedViewCacheKey],
  );

  const cachedSavedViewState = useMemo(
    () => readSavedViewCache(),
    [readSavedViewCache],
  );

  const [savedViewsByScope, setSavedViewsByScope] = useState(
    cachedSavedViewState.views,
  );
  const [activeViewIdByScope, setActiveViewIdByScope] = useState(
    cachedSavedViewState.activeViewIds,
  );
  const [filterPreferenceDetails, setFilterPreferenceDetails] = useState(
    cachedSavedViewState.filterPreferenceDetails,
  );
  const hasRestoredScopeRef = useRef(false);
  const skipInitialFilterApiEffectRef = useRef(true);
  const skipOwnWriteFilterApiEffectRef = useRef(false);

  const fetchSavedViews = useCallback(async () => {
    if (!domain || !subModuleId || !userId) return;

    const response = await fetchFilterPreference(domain, userId, subModuleId);

    if (response.ok && response.responseDetails) {
      const preferenceDetails = response.responseDetails;
      const savedViewDetails =
        preferenceDetails?.filter_condition?.[storageKey] || {};

      setFilterPreferenceDetails(preferenceDetails);
      setSavedViewsByScope(savedViewDetails?.views || {});
      setActiveViewIdByScope(savedViewDetails?.activeViewIds || {});
      writeSavedViewCache(
        savedViewDetails?.views || {},
        savedViewDetails?.activeViewIds || {},
        preferenceDetails,
      );

      // Restore whichever scope (e.g. report-type tab) the user was last
      // on, once per mount, so the saved view they most recently
      // created/activated there is visible by default — activeViewIds is
      // keyed per scope, so without this the table always defaults back
      // to its first scope and a view saved under another one would look
      // like it "disappeared" even though it's still saved fine.
      if (!hasRestoredScopeRef.current) {
        hasRestoredScopeRef.current = true;
        if (savedViewDetails?.lastActiveScope) {
          onRestoreScope?.(savedViewDetails.lastActiveScope);
        }
      }
    } else {
      setFilterPreferenceDetails({});
      setSavedViewsByScope({});
      setActiveViewIdByScope({});
      writeSavedViewCache({}, {}, {});
    }
  }, [
    domain,
    onRestoreScope,
    storageKey,
    subModuleId,
    userId,
    writeSavedViewCache,
  ]);

  useEffect(() => {
    if (subModuleId && userId) {
      fetchSavedViews();
    }
  }, [fetchSavedViews, subModuleId, userId]);

  // `filterApi` is a global redux flag shared by every screen's filter
  // preference save — it exists so this hook picks up changes written by
  // FilterContext's own column-filter/sort save (same backend record,
  // different key inside filter_condition), so a later persist() here
  // doesn't clobber that with a stale cached copy. But the flag defaults to
  // `true` and FilterContext resets it to `false` on every screen mount,
  // which would otherwise fire a redundant duplicate fetch here on load
  // (on top of the mount fetch above) even though nothing actually changed
  // yet. Skip that first firing; only react once the flag genuinely flips
  // later.
  useEffect(() => {
    if (skipInitialFilterApiEffectRef.current) {
      skipInitialFilterApiEffectRef.current = false;
      return;
    }
    // persist() below already applies its own write to local state and sets
    // this ref right before dispatching the flag, so the flip it causes
    // isn't a signal that some OTHER consumer changed the shared record —
    // refetching here would just redundantly re-request the response we
    // already have in hand.
    if (skipOwnWriteFilterApiEffectRef.current) {
      skipOwnWriteFilterApiEffectRef.current = false;
      return;
    }
    if (filterApiRefresh && subModuleId && userId) {
      fetchSavedViews();
    }
  }, [fetchSavedViews, filterApiRefresh, subModuleId, userId]);

  const persist = useCallback(
    async (nextSavedViewsByScope, nextActiveViewIdByScope, successMessage = "") => {
      // Mirrors the read-side guard in fetchSavedViews/the mount effect
      // above -- without it, a screen that forgets to thread a real
      // domain/subModuleId into this hook would silently POST/PUT to
      // `${undefined}filter-preference/`, which the browser resolves as a
      // page-relative URL instead of erroring (e.g.
      // /admin/support/undefinedfilter-preference/, caught live
      // 2026-09-14 via FilterContext's sibling `filter-preference` caller --
      // see the fix there). No caller currently awaits/branches on this
      // function's return value, so returning null here is safe.
      if (!domain || !subModuleId || !userId) return null;

      const payload = {
        user_id: userId,
        submodule_id: subModuleId,
        filter_condition: {
          ...(filterPreferenceDetails?.filter_condition || {}),
          [storageKey]: {
            views: nextSavedViewsByScope,
            activeViewIds: nextActiveViewIdByScope,
            lastActiveScope: scope,
          },
        },
      };

      const requestMethod = filterPreferenceDetails?.id ? "PUT" : "POST";
      const requestUrl = filterPreferenceDetails?.id
        ? `${domain}filter-preference/${filterPreferenceDetails.id}/`
        : `${domain}filter-preference/`;

      const response = await customAPI(requestUrl, requestMethod, payload);

      if (response.ok) {
        const nextFilterPreferenceDetails = {
          ...filterPreferenceDetails,
          id: filterPreferenceDetails?.id || response?.responseDetails?.id,
          filter_condition: payload.filter_condition,
        };
        setSavedViewsByScope(nextSavedViewsByScope);
        setActiveViewIdByScope(nextActiveViewIdByScope);
        setFilterPreferenceDetails(nextFilterPreferenceDetails);
        writeSavedViewCache(
          nextSavedViewsByScope,
          nextActiveViewIdByScope,
          nextFilterPreferenceDetails,
        );
        skipOwnWriteFilterApiEffectRef.current = true;
        dispatch(setFilterApi(true));
        onViewChange?.();

        if (successMessage) {
          showToast("success", successMessage);
        }
      }

      return response;
    },
    [
      dispatch,
      domain,
      filterPreferenceDetails,
      onViewChange,
      scope,
      storageKey,
      subModuleId,
      userId,
      writeSavedViewCache,
    ],
  );

  const savedViewsForScope = useMemo(
    () => savedViewsByScope?.[scope] || [],
    [savedViewsByScope, scope],
  );
  const activeViewId = activeViewIdByScope?.[scope] ?? null;
  const activeView =
    savedViewsForScope.find((view) => view.id === activeViewId) || null;

  const selectView = useCallback(
    (viewId) => {
      persist(savedViewsByScope, { ...activeViewIdByScope, [scope]: viewId });
    },
    [activeViewIdByScope, persist, savedViewsByScope, scope],
  );

  const showAllHeaders = useCallback(() => {
    // "All Headers" is a session-only view of the table, not a saved
    // preference — it's intentionally never written to the backend, so
    // navigating away and back always restores the last saved view that was
    // actually selected/created, never "All Headers", even if that was the
    // very last chip clicked before leaving.
    setActiveViewIdByScope((prev) => ({ ...prev, [scope]: null }));
    onViewChange?.();
  }, [onViewChange, scope]);

  const selectDefaultView = useCallback(() => {
    if (defaultViewId) {
      persist(savedViewsByScope, {
        ...activeViewIdByScope,
        [scope]: defaultViewId,
      });
      return;
    }

    setActiveViewIdByScope((prev) => ({ ...prev, [scope]: null }));
    onViewChange?.();
  }, [
    activeViewIdByScope,
    defaultViewId,
    onViewChange,
    persist,
    savedViewsByScope,
    scope,
  ]);

  const deleteView = useCallback(
    (viewId) => {
      const nextSavedViewsForScope = savedViewsForScope.filter(
        (view) => view.id !== viewId,
      );
      const nextSavedViewsByScope = {
        ...savedViewsByScope,
        [scope]: nextSavedViewsForScope,
      };
      const nextActiveViewIdByScope = {
        ...activeViewIdByScope,
        [scope]:
          activeViewId === viewId ? defaultViewId || null : activeViewId,
      };

      persist(
        nextSavedViewsByScope,
        nextActiveViewIdByScope,
        "View removed successfully.",
      );
    },
    [
      activeViewId,
      activeViewIdByScope,
      defaultViewId,
      persist,
      savedViewsByScope,
      savedViewsForScope,
      scope,
    ],
  );

  const createView = useCallback(
    (viewName, headerConfig, options = {}) => {
      const { activate = true, extraFields = {} } = options;
      const newView = {
        id: `${scope}_${Date.now()}`,
        name: viewName,
        // Ordered [{ key, name }] — drives both column order and per-view
        // label. headerKeys is kept as a plain derived list for any older
        // consumer that only reads that field.
        headerConfig,
        headerKeys: headerConfig.map((entry) => entry.key),
        ...extraFields,
      };
      const nextSavedViewsByScope = {
        ...savedViewsByScope,
        [scope]: [...savedViewsForScope, newView],
      };
      const nextActiveViewIdByScope = {
        ...activeViewIdByScope,
        [scope]: activate ? newView.id : activeViewIdByScope?.[scope] ?? null,
      };

      persist(
        nextSavedViewsByScope,
        nextActiveViewIdByScope,
        "View saved successfully.",
      );

      return newView.id;
    },
    [activeViewIdByScope, persist, savedViewsByScope, savedViewsForScope, scope],
  );

  const upsertView = useCallback(
    (
      viewId,
      viewName,
      headerConfig,
      successMessage = "View updated successfully.",
      extraFields = {},
    ) => {
      const existingView = savedViewsForScope.find((view) => view.id === viewId);
      const nextView = {
        ...(existingView || {}),
        id: viewId,
        name: viewName,
        headerConfig,
        headerKeys: headerConfig.map((entry) => entry.key),
        ...extraFields,
      };

      const nextSavedViewsForScope = existingView
        ? savedViewsForScope.map((view) => (view.id === viewId ? nextView : view))
        : [...savedViewsForScope, nextView];

      const nextSavedViewsByScope = {
        ...savedViewsByScope,
        [scope]: nextSavedViewsForScope,
      };

      persist(nextSavedViewsByScope, activeViewIdByScope, successMessage);
    },
    [
      activeViewIdByScope,
      persist,
      savedViewsByScope,
      savedViewsForScope,
      scope,
    ],
  );

  const updateView = useCallback(
    (viewId, viewName, headerConfig) => {
      const nextSavedViewsForScope = savedViewsForScope.map((view) =>
        view.id === viewId
          ? {
              ...view,
              name: viewName,
              headerConfig,
              headerKeys: headerConfig.map((entry) => entry.key),
            }
          : view,
      );
      const nextSavedViewsByScope = {
        ...savedViewsByScope,
        [scope]: nextSavedViewsForScope,
      };

      persist(
        nextSavedViewsByScope,
        activeViewIdByScope,
        "View updated successfully.",
      );
    },
    [activeViewIdByScope, persist, savedViewsByScope, savedViewsForScope, scope],
  );

  const getVisibleHeaders = useCallback(
    (fullHeaders) => {
      // Grouped tables (two-row header with colSpan'd parents, e.g.
      // salaryRevisionGroupedHeader) store leaf columns nested under
      // `children`. headerConfig/headerKeys always address leaf keys, so
      // flatten to leaves here and, if the source was grouped, regroup the
      // result back into the same {name, children} shape the table expects.
      const grouped = isGroupedHeaderList(fullHeaders);
      const flatFullHeaders = grouped
        ? flattenHeaders(fullHeaders)
        : fullHeaders;

      // Views saved after the reorder/rename update carry an ordered
      // headerConfig ([{ key, name }]) — apply the saved order and the
      // view-specific label, falling back to the header's own name if a
      // column no longer exists in fullHeaders (e.g. a dynamic column that
      // changed since the view was saved).
      if (activeView?.headerConfig?.length) {
        const headerByKey = new Map(
          flatFullHeaders.map((header) => [header.key, header]),
        );
        const orderedHeaders = activeView.headerConfig
          .map((entry) => {
            const baseHeader = headerByKey.get(entry.key);
            if (!baseHeader) return null;
            return {
              ...baseHeader,
              name: entry.name?.trim() || baseHeader.name,
            };
          })
          .filter(Boolean);

        const result = orderedHeaders.length > 0 ? orderedHeaders : flatFullHeaders;
        return grouped ? regroupHeaders(result) : result;
      }

      // Backward compatibility for views saved before reorder/rename
      // support, which only stored an unordered set of header keys.
      if (activeView?.headerKeys?.length) {
        const selectedKeys = new Set(activeView.headerKeys);
        const filteredHeaders = flatFullHeaders.filter((header) =>
          selectedKeys.has(header.key),
        );

        const result =
          filteredHeaders.length > 0 ? filteredHeaders : flatFullHeaders;
        return grouped ? regroupHeaders(result) : result;
      }

      return fullHeaders;
    },
    [activeView],
  );

  return {
    savedViewsForScope,
    activeView,
    activeViewId,
    getVisibleHeaders,
    selectView,
    selectDefaultView,
    showAllHeaders,
    deleteView,
    createView,
    upsertView,
    updateView,
  };
};
